
import React, { useState } from 'react';
import { User, UserSession } from '../types';
import { ICONS } from '../constants';

interface LoginProps {
  users: User[];
  adminPassword: string;
  onLogin: (session: UserSession) => void;
}

const Login: React.FC<LoginProps> = ({ users, adminPassword, onLogin }) => {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const isAdmin = name.trim().toLowerCase() === 'admin';

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanName = name.trim();
    if (!cleanName) return;

    const matchedUser = users.find(u => u.name.toLowerCase() === cleanName.toLowerCase());
    
    if (matchedUser) {
      // Check password
      const expectedPassword = matchedUser.password || (matchedUser.role === 'ADMIN' ? adminPassword : 'spaceshare');
      if (password !== expectedPassword) {
        setError('Incorrect password.');
        return;
      }
      onLogin({ role: matchedUser.role, name: matchedUser.name, userId: matchedUser.id, ownerId: matchedUser.assignedSpaceId });
    } else {
      // Guest login - still require the default password for security if we want, 
      // but user said "default password for uses to 'spaceshare'".
      if (password !== 'spaceshare') {
        setError('Incorrect password.');
        return;
      }
      onLogin({ role: 'QUEUE_LIST', name: cleanName, userId: `guest-${Math.random().toString(36).substr(2, 5)}` });
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-8 duration-700">
        <div className="bg-white rounded-[2.5rem] p-10 md:p-14 shadow-2xl relative">
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-accent-red rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-red-200">
              <ICONS.Car className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-black text-black tracking-tighter">Space<span className="text-accent-red">Share</span></h1>
            <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 bg-slate-50 border border-slate-100 rounded-full">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                System Active & Secure
              </span>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Full Personnel Name</label>
              <div className="relative">
                <ICONS.User className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                <input 
                  type="text" 
                  value={name} 
                  onChange={(e) => { setName(e.target.value); setError(''); }} 
                  placeholder="e.g. George Russell" 
                  className="w-full pl-14 pr-6 py-5 rounded-2xl bg-slate-50 border-2 border-slate-50 focus:border-accent-red outline-none font-bold text-slate-900" 
                  required 
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Password</label>
              <div className="relative">
                <ICONS.Lock className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                <input 
                  type="password" 
                  value={password} 
                  onChange={(e) => { setPassword(e.target.value); setError(''); }} 
                  placeholder="••••••••" 
                  className="w-full pl-14 pr-6 py-5 rounded-2xl bg-slate-50 border-2 border-slate-50 focus:border-accent-red outline-none font-bold text-slate-900" 
                  required 
                />
              </div>
            </div>

            {error && <p className="text-[10px] font-black text-accent-red text-center uppercase tracking-widest">{error}</p>}

            <button type="submit" className="w-full bg-accent-red hover:bg-black text-white font-black py-5 rounded-2xl transition-all shadow-xl shadow-red-100 flex items-center justify-center gap-3 uppercase text-xs tracking-widest">
              Access Dashboard <ICONS.Zap className="w-5 h-5" />
            </button>
          </form>

          <div className="mt-12 space-y-3 opacity-60">
            <p className="text-[9px] text-slate-500 font-black uppercase tracking-[0.2em] text-center">
              Use at your own risk
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
